// src/components/Atelier3/Activite3/Activite3.js
import React, { useState, useEffect } from 'react';
import { Target, Shield, AlertTriangle, Info, ChevronDown, ChevronUp, Search, Eye, Route, Users, Zap } from 'lucide-react';
import { ReactFlowProvider } from 'reactflow';
import { useTranslation } from 'react-i18next';
import { Switch } from '@headlessui/react';
import AttackPathVisualization from './AttackPathVisualization';
import GuideModal from './GuideModal';
import PreliminaryLikelihoodModal from './PreliminaryLikelihoodModal';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier3Store from '../../../stores/useAtelier3Store';
import { useLanguage } from '../../../context/LanguageContext';
import { showLoadingToast, dismissToast, showErrorToast, showSuccessToast } from '../../../utils/toastUtils';
import workshop3Service from '../../../services/workshop3Service'; // Use hydration service
import WorkshopExportMenu from '../../common/WorkshopExportMenu';
import Button from '../../common/Button';
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';

const Activite3 = () => {
  const { t } = useTranslation();
  // State for data
  const [attackPaths, setAttackPaths] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('sourceRiskName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedRows, setExpandedRows] = useState({});
  const [exportLanguage, setExportLanguage] = useState('fr'); // 'fr' or 'en'
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [retentionFilter, setRetentionFilter] = useState('all'); // 'all' | 'retained' | 'hidden' | 'preliminary'
  const [preliminaryTarget, setPreliminaryTarget] = useState(null); // attack path being edited in the modal

  // Silent save: activite3 is a read-only view, resolve immediately
  useSilentSaveListener('atelier3-activite3', () => 'skipped');

  // Get analysis context
  const analysisStore = useAnalysisStore();
  const atelier3Store = useAtelier3Store();
  const currentAnalysis = analysisStore.currentAnalysis;
  const getStakeholders = atelier3Store.loadStakeholders;

  // Get current language from navbar
  const { getCurrentLanguage } = useLanguage();

  // Load data on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (currentAnalysis?.id) {
        setIsLoading(true);
        setError(null);
        const toastId = showLoadingToast(t('workshop3.activity3.loading'));

        try {
          // Load attack paths from hydration service
          console.log('🔄 Loading hydrated attack paths...');
          const response = await workshop3Service.getAttackPaths(currentAnalysis.id);
          console.log('✅ Hydrated attack paths response:', response);
          if (response.success && Array.isArray(response.data)) {
            console.log('🔍 Loaded attack paths:', response.data);
            console.log('🔍 First attack path strategicScenarioReference:', response.data[0]?.strategicScenarioReference);
            setAttackPaths(response.data);
          } else {
            console.error('Invalid attack paths data:', response);
            setAttackPaths([]);
          }

          // Load stakeholders
          try {
            const stakeholdersResponse = await getStakeholders(currentAnalysis.id);
            if (stakeholdersResponse && stakeholdersResponse.success && Array.isArray(stakeholdersResponse.data)) {
              setStakeholders(stakeholdersResponse.data);
            } else {
              console.log('Stakeholders data is not valid:', stakeholdersResponse);
              setStakeholders([]);
            }
          } catch (stakeholderError) {
            console.error('Error loading stakeholders:', stakeholderError);
            setStakeholders([]);
          }

          // Dismiss the loading toast without showing a success toast
          dismissToast(toastId);
        } catch (error) {
          console.error('Error loading data:', error);
          setError(t('workshop3.activity3.errors.loadingError'));
          // Dismiss the loading toast and show error toast
          dismissToast(toastId);
          showErrorToast(t('workshop3.activity3.errors.loadingAttackPathsError'));
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [currentAnalysis?.id]); // Only depend on analysis ID

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Toggle attack path retention
  const handleToggleRetention = async (pathId, retained) => {
    if (!currentAnalysis?.id) return;
    try {
      await workshop3Service.toggleAttackPathRetention(currentAnalysis.id, pathId, retained);
      setAttackPaths(prev =>
        prev.map(p => p.id === pathId ? { ...p, retained } : p)
      );
      showSuccessToast(
        retained
          ? t('workshop3.activity3.retainedOn')
          : t('workshop3.activity3.retainedOff')
      );
    } catch {
      showErrorToast(t('workshop3.activity3.errors.retentionError'));
    }
  };

  // Save preliminary likelihood for an attack path (no operational scenario)
  const handleSavePreliminary = async (data) => {
    if (!currentAnalysis?.id || !preliminaryTarget) return;
    try {
      await workshop3Service.setPreliminaryLikelihood(currentAnalysis.id, preliminaryTarget.id, data);
      setAttackPaths(prev =>
        prev.map(p => p.id === preliminaryTarget.id
          ? { ...p, preliminary: { ...p.preliminary, active: true, likelihood: data.likelihood, justification: data.justification || '' } }
          : p)
      );
      showSuccessToast(t('workshop3.activity3.preliminary.saved'));
      setPreliminaryTarget(null);
    } catch {
      showErrorToast(t('workshop3.activity3.preliminary.saveError'));
    }
  };

  // Clear preliminary mode, back to normal Atelier 4 flow
  const handleClearPreliminary = async () => {
    if (!currentAnalysis?.id || !preliminaryTarget) return;
    try {
      await workshop3Service.clearPreliminaryLikelihood(currentAnalysis.id, preliminaryTarget.id);
      setAttackPaths(prev =>
        prev.map(p => p.id === preliminaryTarget.id
          ? { ...p, preliminary: { active: false } }
          : p)
      );
      showSuccessToast(t('workshop3.activity3.preliminary.cleared'));
      setPreliminaryTarget(null);
    } catch {
      showErrorToast(t('workshop3.activity3.preliminary.saveError'));
    }
  };

  // Filter and sort attack paths
  const filteredAttackPaths = attackPaths
    .filter(path => {
      if (retentionFilter === 'retained') return path.retained;
      if (retentionFilter === 'hidden') return !path.retained;
      if (retentionFilter === 'preliminary') return path.preliminary?.active === true;
      return true;
    })
    .filter(path => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      return (
        (path.sourceRiskName && path.sourceRiskName.toLowerCase().includes(searchLower)) ||
        (path.objectifVise && path.objectifVise.toLowerCase().includes(searchLower)) ||
        (path.dreadedEventName && path.dreadedEventName.toLowerCase().includes(searchLower)) ||
        (path.businessValueName && path.businessValueName.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle specific fields
      if (sortField === 'stakeholders') {
        aValue = a.stakeholders && a.stakeholders.length > 0 ? a.stakeholders[0].name : '';
        bValue = b.stakeholders && b.stakeholders.length > 0 ? b.stakeholders[0].name : '';
      } else if (sortField === 'referenceCode') {
        aValue = a.referenceCode || '';
        bValue = b.referenceCode || '';
      } else if (sortField === 'strategicScenarioReference') {
        aValue = a.strategicScenarioReference || '';
        bValue = b.strategicScenarioReference || '';
      }

      // Default string handling
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Handle null/undefined
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Export to CSV
  const exportToCSV = () => {
    // Define headers based on language
    const headers = exportLanguage === 'en' ? [
      'Scenario',
      'Reference',
      'Risk Source',
      'Target Objective',
      'Feared Event',
      'Business Value',
      'Stakeholders'
    ] : [
      'Scénario',
      'Référence',
      'Source de Risque',
      'Objectif Ciblé',
      'Évènement Redouté',
      'Valeur Métier',
      'Parties Prenantes'
    ];

    const header = headers.join(',');

    // Create CSV rows
    const rows = filteredAttackPaths.map((path, index) => {
      const stakeholderNames = path.stakeholders
        ? path.stakeholders.map(s => s.name).join('; ')
        : '';

      const reference = path.referenceCode || `CA${String(index + 1).padStart(2, '0')}`;
      const scenarioRef = path.strategicScenarioReference || '';

      return [
        `"${scenarioRef}"`,
        `"${reference}"`,
        `"${path.sourceRiskName || ''}"`,
        `"${path.objectifVise || ''}"`,
        `"${path.dreadedEventName || ''}"`,
        `"${path.businessValueName || ''}"`,
        `"${stakeholderNames}"`
      ].join(',');
    });

    // Combine header and rows
    const csv = [header, ...rows].join('\n');

    // Add UTF-8 BOM for proper encoding
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csv;

    // Language-specific filename
    const langSuffix = exportLanguage === 'en' ? 'EN' : 'FR';
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `Attack-Paths-${langSuffix}-${dateStr}.csv`;

    // Create download link
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle PDF Export (FR)
  const handleExportPDF = async () => {
    try {
      const { default: AttackPathsPdfGenerator } = await import('../../../utils/attackPathsPdfGenerator');

      const generator = new AttackPathsPdfGenerator({
        attackPaths: filteredAttackPaths,
        analysisName: currentAnalysis?.name,
        organizationName: currentAnalysis?.companyName,
        analysisId: currentAnalysis?.id
      }, currentAnalysis, 'en');

      const blob = await generator.generateReport();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chemins-attaque-${currentAnalysis?.name || 'export'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccessToast(t('workshop3.activity3.export.success') || 'Rapport PDF généré avec succès');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showErrorToast(t('workshop3.activity3.export.error') || 'Erreur lors de la génération du PDF');
    }
  };

  // Handle PDF Export (EN)
  const handleExportPDFEn = async () => {
    try {
      const { default: AttackPathsPdfGenerator } = await import('../../../utils/attackPathsPdfGenerator');

      const generator = new AttackPathsPdfGenerator({
        attackPaths: filteredAttackPaths,
        analysisName: currentAnalysis?.name,
        organizationName: currentAnalysis?.companyName,
        analysisId: currentAnalysis?.id
      }, currentAnalysis);

      const blob = await generator.generateReport();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attack-paths-${currentAnalysis?.name || 'export'}-EN.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccessToast('PDF Report (EN) generated successfully');
    } catch (error) {
      console.error('Error generating PDF (EN):', error);
      showErrorToast('Error generating PDF (EN)');
    }
  };

  // Handle DOCX Export
  const handleExportDocx = async () => {
    try {
      const { generateAttackPathsDocx } = await import('../../../utils/attackPathsDocxGenerator');

      await generateAttackPathsDocx({
        attackPaths: filteredAttackPaths,
        analysisName: currentAnalysis?.name,
        organizationName: currentAnalysis?.companyName,
        analysisId: currentAnalysis?.id
      }, currentAnalysis);

      showSuccessToast('Rapport Word généré avec succès');
    } catch (error) {
      showErrorToast('Erreur lors de la génération du document Word');
    }
  };

  // Handle English DOCX Export
  const handleExportDocxEn = async () => {
    try {
      const { generateAttackPathsDocx } = await import('../../../utils/attackPathsDocxGenerator');

      await generateAttackPathsDocx(
        {
          attackPaths: filteredAttackPaths,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id
        },
        currentAnalysis,
        'en'
      );

      showSuccessToast('Word Report (EN) generated successfully');
    } catch (error) {
      console.error('Error generating DOCX (EN):', error);
      showErrorToast('Error generating Word (EN) document');
    }
  };


  // Render sort indicator
  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // ── Color helpers ──
  const getNiveauColor = (niveau) => {
    switch (niveau) {
      case 1: return 'bg-green-100 text-green-800 border border-green-300';
      case 2: return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
      case 3: return 'bg-orange-100 text-orange-800 border border-orange-300';
      case 4: return 'bg-red-100 text-red-800 border border-red-300';
      default: return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'minor': return 'bg-emerald-100 text-emerald-800 border border-emerald-300';
      case 'moderate': return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
      case 'major': return 'bg-orange-100 text-orange-800 border border-orange-300';
      case 'critical': return 'bg-red-100 text-red-800 border border-red-300';
      case 'catastrophic': return 'bg-slate-800 text-white border border-slate-700';
      default: return 'bg-gray-100 text-gray-800 border border-gray-300';
    }
  };

  const getTypeBadge = (type) => {
    if (type === 'direct') return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Directe' };
    return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Via PP' };
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Header - Matching Scénarios Stratégiques style */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Left Side: Breadcrumb & Title */}
          <div>
            <div className="flex items-center text-sm text-slate-500 mb-2">
              <span className="hover:text-blue-600 transition-colors">{t('workshop3.title')}</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-blue-600 font-medium">{t('workshop3.activity3.breadcrumb')}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
              <Route size={28} className="mr-3 text-blue-600" />
              {t('workshop3.activity3.title')}
              <span className="ml-3 bg-blue-100 text-blue-700 text-base font-bold px-3 py-1 rounded-full border border-blue-200 shadow-sm">
                {attackPaths.length}
              </span>
            </h1>
          </div>

          {/* Right Side: Action Buttons */}
          <div className="flex items-center space-x-3">
            {/* Export Menu */}
            <WorkshopExportMenu
              onExportPdfFr={handleExportPDF}
              onExportPdfEn={handleExportPDFEn}
              onExportDocxFr={handleExportDocx}
              onExportDocxEn={handleExportDocxEn}
              onExportCsvFr={() => {
                setExportLanguage('fr');
                exportToCSV();
              }}
              onExportCsvEn={() => {
                setExportLanguage('en');
                exportToCSV();
              }}
            />

            {/* Help Button */}
            <Button
              variant="secondary"
              color="white"
              icon={<Info />}
              onClick={() => setShowGuideModal(true)}
              title={t('common.help')}
            >
              {t('common.help')}
            </Button>
          </div>
        </div>

        {/* Description */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-sm text-blue-700">
            {t('workshop3.activity3.description')}
          </p>
        </div>
      </div>

      {/* Guide Modal */}
      <GuideModal isOpen={showGuideModal} onClose={() => setShowGuideModal(false)} />

      {/* Loading state */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-slate-50 rounded-xl shadow-sm">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">{t('workshop3.activity3.loadingMessage')}</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <h3 className="font-bold text-red-800">{t('workshop3.activity3.errors.loadingErrorTitle')}</h3>
              <p className="text-red-700 mt-1">{error}</p>
              <button
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition duration-200 text-sm"
                onClick={() => window.location.reload()}
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="p-5 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  <Target size={20} className="mr-2 text-blue-600" />
                  {t('workshop3.activity3.title')}
                  <span className="ml-3 bg-blue-100 text-blue-700 text-sm font-bold px-2.5 py-0.5 rounded-full border border-blue-200">
                    {filteredAttackPaths.length}
                  </span>
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {t('workshop3.activity3.subtitle')}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                {/* Retention filter pills */}
                <div className="flex items-center bg-slate-100 rounded-lg p-0.5 border border-slate-200">
                  {['all', 'retained', 'hidden', 'preliminary'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => setRetentionFilter(filter)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        retentionFilter === filter
                          ? 'bg-white text-blue-700 shadow-sm border border-slate-200'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {t(`workshop3.activity3.retentionFilter.${filter}`)}
                      {filter === 'all' && (
                        <span className="ml-1 text-slate-400">({attackPaths.length})</span>
                      )}
                      {filter === 'retained' && (
                        <span className="ml-1 text-emerald-500">({attackPaths.filter(p => p.retained).length})</span>
                      )}
                      {filter === 'hidden' && (
                        <span className="ml-1 text-red-400">({attackPaths.filter(p => !p.retained).length})</span>
                      )}
                      {filter === 'preliminary' && (
                        <span className="ml-1 text-purple-500">({attackPaths.filter(p => p.preliminary?.active).length})</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Search input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('workshop3.activity3.searchPlaceholder')}
                    className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full md:w-64 shadow-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                </div>


              </div>
            </div>
          </div>

          {/* Table */}
          {filteredAttackPaths.length === 0 ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 text-blue-500 mb-4">
                <Info size={24} />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t('workshop3.activity3.empty.noAttackPaths')}</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                {t('workshop3.activity3.empty.noAttackPathsMessage')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors duration-150 w-[15%] border-b border-slate-200"
                      onClick={() => handleSort('strategicScenarioReference')}
                    >
                      <div className="flex items-center group">
                        <span className="inline-flex items-center justify-center h-4 w-4 rounded bg-indigo-100 text-indigo-700 font-bold text-[10px] mr-2 flex-shrink-0">
                          S
                        </span>
                        Scénario & Référence
                        <span className={`ml-1.5 p-0.5 rounded ${sortField === 'strategicScenarioReference' ? 'bg-blue-50 text-blue-600' : 'text-slate-300 group-hover:text-slate-500'}`}>
                          {renderSortIndicator('strategicScenarioReference') || <ChevronDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors duration-150 w-[15%] border-b border-slate-200"
                      onClick={() => handleSort('sourceRiskName')}
                    >
                      <div className="flex items-center group">
                        Source Risque
                        <span className={`ml-1.5 p-0.5 rounded ${sortField === 'sourceRiskName' ? 'bg-blue-50 text-blue-600' : 'text-slate-300 group-hover:text-slate-500'}`}>
                          {renderSortIndicator('sourceRiskName') || <ChevronDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors duration-150 w-[11%] border-b border-slate-200"
                      onClick={() => handleSort('objectifVise')}
                    >
                      <div className="flex items-center group">
                        Objectif Visé
                        <span className={`ml-1.5 p-0.5 rounded ${sortField === 'objectifVise' ? 'bg-blue-50 text-blue-600' : 'text-slate-300 group-hover:text-slate-500'}`}>
                          {renderSortIndicator('objectifVise') || <ChevronDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-[10%] border-b border-slate-200"
                    >
                      <div className="flex items-center group">
                        {t('workshop3.activity3.preliminary.colHeader')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors duration-150 w-[15%] border-b border-slate-200"
                      onClick={() => handleSort('dreadedEventName')}
                    >
                      <div className="flex items-center group">
                        Évènement Redouté
                        <span className={`ml-1.5 p-0.5 rounded ${sortField === 'dreadedEventName' ? 'bg-blue-50 text-blue-600' : 'text-slate-300 group-hover:text-slate-500'}`}>
                          {renderSortIndicator('dreadedEventName') || <ChevronDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors duration-150 w-[12%] border-b border-slate-200"
                      onClick={() => handleSort('businessValueName')}
                    >
                      <div className="flex items-center group">
                        Valeur Métier
                        <span className={`ml-1.5 p-0.5 rounded ${sortField === 'businessValueName' ? 'bg-blue-50 text-blue-600' : 'text-slate-300 group-hover:text-slate-500'}`}>
                          {renderSortIndicator('businessValueName') || <ChevronDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                        </span>
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors duration-150 w-[12%] border-b border-slate-200"
                      onClick={() => handleSort('stakeholders')}
                    >
                      <div className="flex items-center group">
                        Parties Prenantes
                        <span className={`ml-1.5 p-0.5 rounded ${sortField === 'stakeholders' ? 'bg-blue-50 text-blue-600' : 'text-slate-300 group-hover:text-slate-500'}`}>
                          {renderSortIndicator('stakeholders') || <ChevronDown size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                        </span>
                      </div>
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-[10%] border-b border-slate-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredAttackPaths.flatMap((path) => {
                    const pathIndex = filteredAttackPaths.indexOf(path);
                    const isNoStakeholders = !path.stakeholders || path.stakeholders.length === 0;
                    const pathRows = [];

                    // If there are no stakeholders, create one row
                    if (isNoStakeholders) {
                      pathRows.push(
                        <tr
                          key={`${path.id}-none`}
                          className={`hover:bg-blue-50 transition-colors duration-150 ${expandedRows[path.id] ? 'bg-slate-50' : ''}`}
                        >
                          <td className="px-4 py-3 whitespace-normal break-words">
                            <div className="flex flex-col space-y-1.5">
                              {path.strategicScenarioReference && (
                                <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-100 w-max max-w-full">
                                  <span className="truncate">{path.strategicScenarioReference}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <div className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium w-max border bg-white text-slate-700 border-slate-200 shadow-sm">
                                  <span className="truncate">{path.referenceCode || `CA${String(pathIndex + 1).padStart(2, '0')}`}</span>
                                </div>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border ${getTypeBadge(path.type).bg} ${getTypeBadge(path.type).text} ${getTypeBadge(path.type).border}`}>
                                  {path.type === 'direct' ? 'DIR' : 'PP'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-normal break-words">
                            <div className="flex flex-col gap-1">
                              <div className="text-sm font-medium text-slate-800 line-clamp-2">{path.sourceRiskName}</div>
                              {path.niveau && (
                                <span className={`inline-flex items-center self-start px-2 py-0.5 text-[10px] font-extrabold rounded-md shadow-sm ${getNiveauColor(path.niveau)}`}>
                                  N{path.niveau}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-normal break-words">
                            <div className="text-sm text-slate-600 line-clamp-3">{path.objectifVise}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => setPreliminaryTarget(path)}
                              title={t('workshop3.activity3.preliminary.button')}
                              className="inline-flex items-center gap-1.5 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors min-w-[100px]"
                            >
                              {path.preliminary?.active ? (
                                <span className="inline-flex items-center gap-1 w-full justify-center bg-purple-100 text-purple-800 border border-purple-200 rounded-md py-0.5 px-1.5">
                                  <Zap size={11} />
                                  V{path.preliminary.likelihood} <span className="font-bold">·</span> {t('workshop3.activity3.preliminary.badge')}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-600">
                                  <Zap size={12} />
                                  {t('workshop3.activity3.preliminary.define')}
                                </span>
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-normal break-words">
                            <div className="flex flex-col gap-1.5">
                              <div className="text-sm font-medium text-amber-700 bg-amber-50 px-2.5 py-2 rounded-md border border-amber-100 flex items-start w-full">
                                <AlertTriangle size={14} className="text-amber-500 mr-2 flex-shrink-0 relative top-0.5" />
                                <span className="leading-snug break-words">{path.dreadedEventName}</span>
                              </div>
                              {path.severity && (
                                <span className={`inline-flex items-center self-start px-2 py-0.5 text-[10px] font-bold rounded shadow-sm ${getSeverityColor(path.severity)}`}>
                                  {t(`severity.${path.severity}`)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-normal break-words">
                            <div className="flex flex-col gap-1">
                              <div className="text-sm text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100 font-medium text-center">
                                {path.businessValueName}
                              </div>
                              {path.supportingAssets && path.supportingAssets.length > 0 && (
                                <span className="text-[10px] text-slate-500 text-center">
                                  {path.supportingAssets.length} actif(s) support
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-normal break-words">
                            <span className="text-slate-400 italic text-[11px] bg-slate-50 px-2 py-1 rounded border border-slate-100 block w-full text-center">{t('workshop3.activity3.table.none')}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                            <div className="flex justify-end space-x-1">
                              <div className="flex items-center gap-1.5">
                                <Switch
                                  checked={path.retained}
                                  onChange={(checked) => handleToggleRetention(path.id, checked)}
                                  title={path.retained ? t('workshop3.activity3.retainedOn') : t('workshop3.activity3.retainedOff')}
                                  className={`${path.retained ? 'bg-emerald-500' : 'bg-slate-300'} relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1`}
                                >
                                  <span className={`${path.retained ? 'translate-x-[18px]' : 'translate-x-[2px]'} inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`} />
                                </Switch>
                                <span className={`text-[10px] uppercase tracking-wider font-semibold ${path.retained ? 'text-emerald-600' : 'text-slate-400'}`}>
                                  {path.retained ? t('workshop3.activity3.retainedLabel') : t('workshop3.activity3.retainedOffLabel')}
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  if (!expandedRows[path.id]) toggleRowExpansion(path.id);
                                  setTimeout(() => {
                                    const el = document.getElementById(`expanded-${path.id}`);
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }, 200);
                                }}
                                className="text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-800 flex items-center p-2 rounded-lg transition-colors border border-blue-100"
                                title={t('workshop3.activity3.visualizeAttackPath')}
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => toggleRowExpansion(path.id)}
                                className="text-slate-500 bg-white hover:bg-slate-50 hover:text-slate-800 flex items-center p-2 rounded-lg transition-colors border border-slate-200 shadow-sm"
                                title="Détails"
                              >
                                {expandedRows[path.id] ? (
                                  <ChevronUp size={16} />
                                ) : (
                                  <ChevronDown size={16} />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    } else {
                      // If there are stakeholders, create one row per stakeholder
                      path.stakeholders.forEach((stakeholder, index) => {
                        pathRows.push(
                          <tr
                            key={`${path.id}-${stakeholder.id}`}
                            className={`hover:bg-blue-50 transition-colors duration-150 ${expandedRows[path.id] ? 'bg-slate-50' : ''} ${index > 0 ? 'border-t-0' : ''}`}
                          >
                            <td className="px-4 py-3 whitespace-normal break-words">
                              {index === 0 && (
                                <div className="flex flex-col space-y-1.5">
                                  {path.strategicScenarioReference && (
                                    <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-100 w-max max-w-full">
                                      <span className="truncate">{path.strategicScenarioReference}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <div className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium w-max border bg-white text-slate-700 border-slate-200 shadow-sm">
                                      <span className="truncate">{path.referenceCode || `CA${String(pathIndex + 1).padStart(2, '0')}`}</span>
                                    </div>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border ${getTypeBadge(path.type).bg} ${getTypeBadge(path.type).text} ${getTypeBadge(path.type).border}`}>
                                      {path.type === 'direct' ? 'DIR' : 'PP'}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-normal break-words">
                              <div className="flex flex-col gap-1">
                                <div className="text-sm font-medium text-slate-800 line-clamp-2">{path.sourceRiskName}</div>
                                {path.niveau && (
                                  <span className={`inline-flex items-center self-start px-2 py-0.5 text-[10px] font-extrabold rounded-md shadow-sm ${getNiveauColor(path.niveau)}`}>
                                    N{path.niveau}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-normal break-words">
                              <div className="text-sm text-slate-600 line-clamp-3">{path.objectifVise}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <button
                                onClick={() => setPreliminaryTarget(path)}
                                title={t('workshop3.activity3.preliminary.button')}
                                className="inline-flex items-center gap-1.5 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors min-w-[100px]"
                              >
                                {path.preliminary?.active ? (
                                  <span className="inline-flex items-center gap-1 w-full justify-center bg-purple-100 text-purple-800 border border-purple-200 rounded-md py-0.5 px-1.5">
                                    <Zap size={11} />
                                    V{path.preliminary.likelihood} <span className="font-bold">·</span> {t('workshop3.activity3.preliminary.badge')}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-600">
                                    <Zap size={12} />
                                    {t('workshop3.activity3.preliminary.define')}
                                  </span>
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3 whitespace-normal break-words">
                              <div className="flex flex-col gap-1.5">
                                <div className="text-sm font-medium text-amber-700 bg-amber-50 px-2.5 py-2 rounded-md border border-amber-100 flex items-start w-full">
                                  <AlertTriangle size={14} className="text-amber-500 mr-2 flex-shrink-0 relative top-0.5" />
                                  <span className="leading-snug break-words">{path.dreadedEventName}</span>
                                </div>
                                {path.severity && (
                                  <span className={`inline-flex items-center self-start px-2 py-0.5 text-[10px] font-bold rounded shadow-sm ${getSeverityColor(path.severity)}`}>
                                    {t(`severity.${path.severity}`)}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-normal break-words">
                              <div className="flex flex-col gap-1">
                                <div className="text-sm text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100 font-medium text-center">
                                  {path.businessValueName}
                                </div>
                                {path.supportingAssets && path.supportingAssets.length > 0 && (
                                  <span className="text-[10px] text-slate-500 text-center">
                                    {path.supportingAssets.length} actif(s) support
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-normal break-words">
                              <div
                                className="inline-flex items-center justify-center px-2.5 py-1 rounded border shadow-sm text-xs font-medium bg-white text-slate-700 border-slate-200 w-full"
                              >
                                <span className="w-2 h-2 rounded-full bg-teal-500 mr-2 flex-shrink-0"></span>
                                <span className="truncate">{stakeholder.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                              <div className="flex justify-end space-x-1">
                                <div className="flex items-center gap-1.5">
                                  <Switch
                                    checked={path.retained}
                                    onChange={(checked) => handleToggleRetention(path.id, checked)}
                                    title={path.retained ? t('workshop3.activity3.retainedOn') : t('workshop3.activity3.retainedOff')}
                                    className={`${path.retained ? 'bg-emerald-500' : 'bg-slate-300'} relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1`}
                                  >
                                    <span className={`${path.retained ? 'translate-x-[18px]' : 'translate-x-[2px]'} inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`} />
                                  </Switch>
                                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${path.retained ? 'text-emerald-600' : 'text-slate-400'}`}>
                                    {path.retained ? t('workshop3.activity3.retainedLabel') : t('workshop3.activity3.retainedOffLabel')}
                                  </span>
                                </div>
                                <button
                                  onClick={() => {
                                    if (!expandedRows[path.id]) toggleRowExpansion(path.id);
                                    setTimeout(() => {
                                      const el = document.getElementById(`expanded-${path.id}`);
                                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 200);
                                  }}
                                  className="text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-800 flex items-center p-2 rounded-lg transition-colors border border-blue-100"
                                  title={t('workshop3.activity3.visualizeAttackPath')}
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  onClick={() => toggleRowExpansion(path.id)}
                                  className="text-slate-500 bg-white hover:bg-slate-50 hover:text-slate-800 flex items-center p-2 rounded-lg transition-colors border border-slate-200 shadow-sm"
                                  title="Détails"
                                >
                                  {expandedRows[path.id] ? (
                                    <ChevronUp size={16} />
                                  ) : (
                                    <ChevronDown size={16} />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    }

                    // Add expanded details row if this path is expanded
                    if (expandedRows[path.id]) {
                      pathRows.push(
                        <tr key={`${path.id}-expanded`} className="bg-slate-50">
                          <td colSpan="8" className="px-4 py-6" id={`expanded-${path.id}`}>
                            <div className="space-y-5">
                              {/* Rich Summary Card */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Source Risk & Objective */}
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                  <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center">
                                    <Target size={16} className="mr-1.5 text-blue-600" />
                                    {t('workshop3.activity3.table.riskSource')}
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <p><span className="font-medium text-slate-600">Nom :</span> {path.sourceRiskName}</p>
                                    <p><span className="font-medium text-slate-600">{t('workshop3.activity3.table.targetObjective')} :</span> {path.objectifVise}</p>
                                    {path.niveau && (
                                      <p>
                                        <span className="font-medium text-slate-600">Niveau :</span>{' '}
                                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-extrabold rounded-md ${getNiveauColor(path.niveau)}`}>
                                          {path.niveau}
                                        </span>
                                      </p>
                                    )}
                                    <p>
                                      <span className="font-medium text-slate-600">Type :</span>{' '}
                                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded border ${getTypeBadge(path.type).bg} ${getTypeBadge(path.type).text} ${getTypeBadge(path.type).border}`}>
                                        {path.type === 'direct' ? 'Attaque directe' : 'Via partie prenante'}
                                      </span>
                                    </p>
                                  </div>
                                </div>

                                {/* Feared Event & Severity */}
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                  <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center">
                                    <AlertTriangle size={16} className="mr-1.5 text-amber-600" />
                                    {t('workshop3.activity3.table.dreadedEvent')}
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <p><span className="font-medium text-slate-600">Nom :</span> {path.dreadedEventName}</p>
                                    {path.severity && (
                                      <p>
                                        <span className="font-medium text-slate-600">Gravité :</span>{' '}
                                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded shadow-sm ${getSeverityColor(path.severity)}`}>
                                          {t(`severity.${path.severity}`)}
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {/* Business Value & Stakeholders */}
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                  <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center">
                                    <Shield size={16} className="mr-1.5 text-emerald-600" />
                                    {t('workshop3.activity3.table.businessValue')}
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <p><span className="font-medium text-slate-600">Nom :</span> {path.businessValueName}</p>
                                    {path.supportingAssets && path.supportingAssets.length > 0 && (
                                      <div>
                                        <span className="font-medium text-slate-600">Actifs support ({path.supportingAssets.length}) :</span>
                                        <ul className="mt-1 space-y-0.5">
                                          {path.supportingAssets.slice(0, 5).map(asset => (
                                            <li key={asset.id} className="text-xs text-slate-600 ml-2">• {asset.name}</li>
                                          ))}
                                          {path.supportingAssets.length > 5 && (
                                            <li className="text-xs text-slate-400 ml-2">+{path.supportingAssets.length - 5} autres...</li>
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                  </div>

                                  {/* Stakeholders */}
                                  <div className="mt-3 pt-3 border-t border-slate-100">
                                    <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center">
                                      <Users size={14} className="mr-1.5 text-teal-600" />
                                      {t('workshop3.activity3.detailsModal.concernedStakeholder')}
                                    </h4>
                                    {path.stakeholders && path.stakeholders.length > 0 ? (
                                      <ul className="space-y-1">
                                        {path.stakeholders.map(s => (
                                          <li key={s.id} className="flex items-center text-sm text-slate-700">
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-100 text-teal-800 text-[10px] font-bold mr-2 flex-shrink-0">
                                              {s.name.charAt(0)}
                                            </span>
                                            {s.name}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="text-xs text-slate-400 italic">Aucune partie prenante</p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Inline Flow Visualization */}
                              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                                <ReactFlowProvider>
                                  <AttackPathVisualization
                                    attackPath={path}
                                    title={t('workshop3.activity3.visualization.title', {
                                      sourceRisk: path.sourceRiskName,
                                      dreadedEvent: path.dreadedEventName
                                    })}
                                    onSave={(updatedPath) => {
                                      setAttackPaths(prev =>
                                        prev.map(p => p.id === updatedPath.id ? updatedPath : p)
                                      );
                                    }}
                                  />
                                </ReactFlowProvider>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return pathRows;
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Preliminary likelihood modal */}
      {preliminaryTarget && (
        <PreliminaryLikelihoodModal
          path={preliminaryTarget}
          analysisId={currentAnalysis?.id}
          onClose={() => setPreliminaryTarget(null)}
          onSave={handleSavePreliminary}
          onClear={handleClearPreliminary}
        />
      )}
    </div>
  );
};

export default Activite3;
